import {
  channelDataSetAccents,
  channelDataSetDerivedAccents,
  coerceFieldValue,
  referenceObjectTypeIdFromConfig,
  type ChannelDataSetAccent,
} from "@mlx/contracts";

import type {
  WorkspaceField,
  WorkspaceObjectType,
  WorkspaceRecord,
} from "@/lib/mlx-workspace-client";
import { formatFieldDate } from "@/lib/format";

import { amountValue, percentDisplayValue } from "./record-amounts";

export { amountValue } from "./record-amounts";
export { formatFieldDate, parseFieldDate } from "@/lib/format";

export type EditSelection =
  | {
      mode: "create";
      /** Draft values a surface seeds the editor with, keyed by field key:
       *  a calendar day that was clicked, a board column that was chosen. */
      presetValues?: Record<string, string>;
    }
  | { mode: "edit"; record: WorkspaceRecord };

export type FieldOption = {
  value: string;
  label: string;
  color: ChannelDataSetAccent;
};

export const FIELD_OPTION_COLORS = channelDataSetAccents;

function isFieldOptionColor(value: unknown): value is ChannelDataSetAccent {
  return (
    typeof value === "string" &&
    (FIELD_OPTION_COLORS as readonly string[]).includes(value)
  );
}

export function defaultFieldOptionColor(index: number): ChannelDataSetAccent {
  return channelDataSetDerivedAccents[
    index % channelDataSetDerivedAccents.length
  ]!;
}

export function cellValue(
  record: WorkspaceRecord,
  field: WorkspaceField,
): string {
  const value = record.values?.[field.key];
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return JSON.stringify(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function fieldOptionEntries(
  field: Pick<WorkspaceField, "config">,
): FieldOption[] {
  const raw = field.config?.options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((option, index) => {
      if (option && typeof option === "object") {
        const record = option as {
          color?: unknown;
          label?: unknown;
          value?: unknown;
        };
        const value = String(record.value ?? record.label ?? "").trim();
        const label = String(record.label ?? record.value ?? "").trim();
        const configured = field.config?.optionColors?.[value];
        const color = isFieldOptionColor(configured)
          ? configured
          : isFieldOptionColor(record.color)
            ? record.color
            : defaultFieldOptionColor(index);
        return value && label ? { value, label, color } : null;
      }
      const value = String(option ?? "").trim();
      const configured = field.config?.optionColors?.[value];
      const color = isFieldOptionColor(configured)
        ? configured
        : defaultFieldOptionColor(index);
      return value ? { value, label: value, color } : null;
    })
    .filter((option): option is FieldOption => option !== null);
}

export function configWithFieldOptionColor(
  field: Pick<WorkspaceField, "config">,
  optionValue: string,
  color: ChannelDataSetAccent,
): NonNullable<WorkspaceField["config"]> {
  const target = optionValue.toLowerCase();
  return {
    ...field.config,
    optionColors: Object.fromEntries(
      fieldOptionEntries(field).map((option) => [
        option.value,
        option.value.toLowerCase() === target ? color : option.color,
      ]),
    ),
  };
}

export function fieldOptions(field: WorkspaceField): string[] {
  return fieldOptionEntries(field).map((option) => option.value);
}

export function fieldOptionLabel(
  field: Pick<WorkspaceField, "config">,
  value: unknown,
): string {
  const raw = String(value ?? "");
  return (
    fieldOptionEntries(field).find(
      (option) => option.value.toLowerCase() === raw.toLowerCase(),
    )?.label ?? raw
  );
}

export function referenceObjectTypeId(
  field: Pick<WorkspaceField, "config">,
): string | null {
  return referenceObjectTypeIdFromConfig(field.config) ?? null;
}

export function referenceOptions(
  field: WorkspaceField,
  records: WorkspaceRecord[],
): WorkspaceRecord[] {
  const targetId = referenceObjectTypeId(field);
  if (!targetId) return [];
  return records
    .filter(
      (record) =>
        record.status === "active" && record.objectTypeId === targetId,
    )
    .sort((left, right) => left.title.localeCompare(right.title));
}

export function isSystemOwnedField(
  field: Pick<WorkspaceField, "config">,
): boolean {
  return field.config?.valueOwner === "source" || field.config?.valueOwner === "workflow";
}

export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) {
    const items = value
      .map(displayValue)
      .filter((item) => item && item !== "-");
    return items.length ? items.join(", ") : "-";
  }
  if (typeof value === "object") return objectDisplayValue(value);
  return String(value);
}

function objectDisplayValue(value: object): string {
  const record = value as Record<string, unknown>;
  const primary = firstObjectText(record, [
    "projectName",
    "name",
    "title",
    "label",
    "summary",
  ]);
  const secondary = firstObjectText(record, [
    "projectCode",
    "code",
    "number",
    "id",
  ]);
  if (primary && secondary && primary !== secondary)
    return `${primary} (${secondary})`;
  if (primary) return primary;
  if (secondary) return secondary;
  return JSON.stringify(value);
}

function currencyPrecision(
  config: WorkspaceField["config"] | undefined,
): number | undefined {
  const precision = config?.precision;
  if (
    typeof precision !== "number" ||
    !Number.isInteger(precision) ||
    precision < 0 ||
    precision > 6
  ) {
    return undefined;
  }
  return precision;
}

function firstObjectText(
  record: Record<string, unknown>,
  keys: string[],
): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value))
      return String(value);
  }
  return "";
}

/** Short-valued fields sit two to a row in the record form. */
export function isCompactFormField(
  field: Pick<WorkspaceField, "type">,
): boolean {
  return [
    "select",
    "date",
    "datetime",
    "boolean",
    "number",
    "currency",
    "percent",
    "user",
  ].includes(field.type);
}

export function formatFieldValue(
  field: Pick<WorkspaceField, "type" | "config">,
  value: unknown,
  references?: ReadonlyMap<string, WorkspaceRecord>,
): string {
  if (value === null || value === undefined || value === "") return "-";
  if (field.type === "boolean") {
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false") return "No";
    return displayValue(value);
  }
  if (field.type === "date" || field.type === "datetime") {
    return (
      formatFieldDate(value, field.type === "datetime") ?? displayValue(value)
    );
  }
  if (field.type === "reference" && typeof value === "string") {
    return references?.get(value)?.title || "Linked record unavailable";
  }
  if (field.type === "select") return fieldOptionLabel(field, value);
  if (field.type === "multiselect" && Array.isArray(value)) {
    return value.map((item) => fieldOptionLabel(field, item)).join(", ");
  }
  if (field.type === "currency") {
    const numeric = amountValue(value);
    const currency =
      typeof field.config?.currency === "string"
        ? field.config.currency
        : "GBP";
    // A field may pin its decimal places (finance views need pence on every
    // row); without that, large amounts stay compact as before.
    const precision = currencyPrecision(field.config);
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      ...(precision === undefined
        ? { maximumFractionDigits: Math.abs(numeric) >= 1000 ? 0 : 2 }
        : {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
          }),
    }).format(numeric);
  }
  if (field.type === "percent") {
    const numeric = percentDisplayValue(value, field.config?.percentScale);
    return `${numeric.toLocaleString("en-GB", {
      maximumFractionDigits: 1,
      minimumFractionDigits: Number.isInteger(numeric) ? 0 : 1,
    })}%`;
  }
  return displayValue(value);
}

export function recordTitle(
  type: WorkspaceObjectType | null,
  fields: WorkspaceField[],
  values: Record<string, unknown>,
  explicitTitle?: string,
): string {
  const primaryField = fields.find(isPrimaryField);
  if (primaryField) {
    const fromPrimary = displayValue(values[primaryField.key]);
    if (fromPrimary && fromPrimary !== "-") return fromPrimary.slice(0, 500);
  }

  const title = explicitTitle?.trim();
  if (title) return title.slice(0, 500);

  const identity =
    type?.slug === "invoice"
      ? "number"
      : type?.slug === "task"
        ? "title"
        : "name";
  const identityField =
    fields.find((field) => field.key === identity) ??
    fields.find((field) => ["name", "number", "title"].includes(field.key));
  const fromIdentity = identityField
    ? displayValue(values[identityField.key])
    : "";
  if (fromIdentity && fromIdentity !== "-") return fromIdentity.slice(0, 500);

  const first = Object.values(values)
    .map(displayValue)
    .find((value) => value && value !== "-");
  return (first ?? type?.name ?? "New record").slice(0, 500);
}

export function isPrimaryField(field: WorkspaceField): boolean {
  return field.config?.primary === true;
}

export function isMultilineField(field: WorkspaceField): boolean {
  return field.type === "text" && field.config?.display === "multiline";
}

export function validateRequired(
  fields: WorkspaceField[],
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (field.required && !values[field.key]?.trim()) {
      errors[field.key] = "Required";
    }
  }
  return errors;
}

export function valuesFromDraft(
  fields: WorkspaceField[],
  draft: Record<string, string>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = draft[field.key] ?? "";
    if (!raw.trim() && !field.required) continue;
    values[field.key] = coerceFieldValue(field.type, raw);
  }
  return values;
}

export function moneyField(fields: WorkspaceField[]): WorkspaceField | null {
  return (
    fields.find((field) => field.type === "currency") ??
    fields.find((field) => ["number", "percent"].includes(field.type) &&
      ["amount", "value", "total", "price", "revenue", "cost", "profit"].some(
        (term) =>
          field.key.toLowerCase().includes(term) ||
          field.label.toLowerCase().includes(term),
      ),
    ) ??
    null
  );
}

export function boardGroupField(
  fields: WorkspaceField[],
): WorkspaceField | null {
  const selects = fields.filter((field) => field.type === "select");
  const preferred = ["status", "stage", "state", "pipeline"];
  return (
    selects.find((field) =>
      preferred.some(
        (term) =>
          field.key.toLowerCase().includes(term) ||
          field.label.toLowerCase().includes(term),
      ),
    ) ??
    selects[0] ??
    null
  );
}

export function chipFields(
  fields: WorkspaceField[],
  group: WorkspaceField,
  sumField: WorkspaceField | null,
): WorkspaceField[] {
  const skip = new Set([
    "name",
    "number",
    "title",
    group.key,
    sumField?.key ?? "",
  ]);
  return fields
    .filter((field) => !skip.has(field.key))
    .filter((field) => !isPrimaryField(field))
    .filter((field) => field.type !== "json" && field.type !== "file")
    .slice(0, 2);
}

export function columnColor(option: string, index: number): string {
  const lowered = option.toLowerCase();
  if (
    lowered.includes("done") ||
    lowered.includes("paid") ||
    lowered.includes("complete")
  )
    return "#16a34a";
  if (
    lowered.includes("overdue") ||
    lowered.includes("fail") ||
    lowered.includes("block")
  )
    return "#e11d48";
  if (lowered.includes("draft")) return "#64748b";
  if (
    lowered.includes("sent") ||
    lowered.includes("progress") ||
    lowered.includes("working")
  )
    return "#f59e0b";
  const palette = ["#0ea5e9", "#8b5cf6", "#14b8a6", "#ec4899", "#6366f1"];
  return palette[index % palette.length]!;
}
